import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaPhone, FaEye, FaEyeSlash, FaLock, FaUser } from 'react-icons/fa';
import toast from 'react-hot-toast';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    const cleanPhone = phoneNumber.replace(/\D/g, '');
    if (!phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (cleanPhone.length !== 10) {
      newErrors.phoneNumber = 'Please enter a valid 10-digit phone number';
    } else if (!/^[6-9]\d{9}$/.test(cleanPhone)) {
      newErrors.phoneNumber = 'Please enter a valid Indian mobile number';
    }
    
    if (!password.trim()) {
      newErrors.password = 'Password is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors above');
      return;
    }
    
    setLoading(true);
    
    try {
      const result = await login({
        phoneNumber: phoneNumber.replace(/\D/g, ''),
        password: password
      });
      
      if (result.success) {
        toast.success('Welcome back!');
        navigate('/products');
      } else {
        toast.error(result.message || 'Login failed. Please try again.');
      }
    } catch (error) {
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (value) => {
    const numbers = value.replace(/\D/g, '');
    // Only allow 10 digits for Indian numbers
    if (numbers.length > 10) {
      return numbers.slice(0, 10);
    }
    return numbers;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhoneNumber(formatted);
    if (errors.phoneNumber) {
      setErrors({ ...errors, phoneNumber: '' });
    }
  };

  return (
    <div className="container">
      <div className="row justify-content-center">
        <div className="col-md-6 col-lg-5">
          <div className="card mt-5">
            <div className="card-body p-6">
              <div className="text-center mb-6">
                <div className="mb-4">
                  <div className="bg-primary rounded-circle d-inline-flex align-items-center justify-content-center" 
                       style={{ width: '60px', height: '60px' }}>
                    <FaLock className="text-white" style={{ fontSize: '1.5rem' }} />
                  </div>
                </div>
                <h1 className="h2 mb-2">Welcome Back</h1>
                <p className="text-secondary">Sign in to your account to continue</p>
              </div>
              
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="phoneNumber">
                    <FaPhone className="me-2" />
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    className={`form-control ${errors.phoneNumber ? 'is-invalid' : ''}`}
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    placeholder="Enter 10-digit mobile number"
                    maxLength="10"
                    required
                    disabled={loading}
                  />
                  {errors.phoneNumber && (
                    <div className="invalid-feedback d-block">
                      {errors.phoneNumber}
                    </div>
                  )}
                </div>
                
                <div className="form-group">
                  <label htmlFor="password">
                    <FaLock className="me-2" />
                    Password
                  </label>
                  <div className="input-group">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      required
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={loading}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  {errors.password && (
                    <div className="invalid-feedback d-block">
                      {errors.password}
                    </div>
                  )}
                </div>
                
                <button
                  type="submit"
                  className="btn btn-primary w-100 btn-lg"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      Signing in...
                    </>
                  ) : (
                    <>
                      <FaLock className="me-2" />
                      Sign In
                    </>
                  )}
                </button>
              </form>
              
              <div className="text-center mt-6">
                <p className="mb-3">
                  Don't have an account?{' '}
                  <Link to="/register" className="text-primary fw-bold">
                    Create one here
                  </Link>
                </p>
              </div>
            </div>
          </div>
          
          {/* Features Section */}
          <div className="row mt-6">
            <div className="col-4 text-center">
              <div className="p-3">
                <div className="text-primary mb-2">
                  <FaPhone style={{ fontSize: '1.5rem' }} />
                </div>
                <p className="text-sm text-secondary mb-0">Quick Login</p>
              </div>
            </div>
            <div className="col-4 text-center">
              <div className="p-3">
                <div className="text-success mb-2">
                  <FaLock style={{ fontSize: '1.5rem' }} />
                </div>
                <p className="text-sm text-secondary mb-0">Secure</p>
              </div>
            </div>
            <div className="col-4 text-center">
              <div className="p-3">
                <div className="text-accent mb-2">
                  <FaUser style={{ fontSize: '1.5rem' }} />
                </div>
                <p className="text-sm text-secondary mb-0">Personalized</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login; 